import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { redirectTopLevel } from "@/lib/redirect";
import { Loader2, CreditCard, ExternalLink, AlertCircle, CheckCircle, AlertTriangle } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

export default function BillingPanel() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [opening, setOpening] = useState(false);

  useEffect(() => {
    api.get("/billing/status")
      .then((r) => setStatus(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const openPortal = async () => {
    setOpening(true);
    try {
      const { data } = await api.post("/billing/portal");
      redirectTopLevel(data.url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not open billing portal");
      setOpening(false);
    }
  };

  if (loading) return null;

  const active = status?.active;
  const needsUpdate = status?.needs_payment_update;
  const canManage = !!status?.status; // has any subscription record, even past_due/canceled

  return (
    <div className="brutal-border bg-[#0a0a0a] p-6" data-testid="billing-panel">
      <div className="flex items-center gap-3 mb-4">
        <CreditCard className={`w-5 h-5 ${active ? "text-[#00FF66]" : needsUpdate ? "text-[#FF3333]" : "text-white/40"}`} />
        <h2 className="font-heading uppercase text-xl">Membership</h2>
      </div>

      {needsUpdate ? (
        <>
          <div className="brutal-border-red bg-[#FF3333]/10 p-4 mb-4 flex items-start gap-3" data-testid="billing-payment-required">
            <AlertTriangle className="w-5 h-5 text-[#FF3333] flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-mono text-[10px] tracking-[0.3em] text-[#FF3333]">UPDATE PAYMENT</div>
              <div className="font-sub text-sm text-white/85 mt-1">
                Your last payment didn&apos;t go through ({status.status}). Update your card to restore membership access.
              </div>
            </div>
          </div>
          <button
            onClick={openPortal}
            disabled={opening}
            data-testid="billing-portal-btn"
            className="btn-forged-red w-full justify-center disabled:opacity-50"
          >
            {opening ? <><Loader2 className="w-4 h-4 animate-spin" /> Opening</> : <><ExternalLink className="w-4 h-4" /> Update Payment Method</>}
          </button>
        </>
      ) : active ? (
        <>
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="w-4 h-4 text-[#00FF66]" />
            <div className="font-mono text-[10px] tracking-[0.3em] text-[#00FF66]">
              {status.status?.toUpperCase()} · MONTHLY MEMBERSHIP
            </div>
          </div>
          {status.cancel_at_period_end && (
            <div className="brutal-border-red bg-[#FF3333]/5 p-3 mt-3 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-[#FF3333] mt-0.5" />
              <div className="font-sub text-sm text-white/80">
                Cancellation scheduled. Access continues until{" "}
                {status.current_period_end
                  ? new Date(status.current_period_end * 1000).toLocaleDateString()
                  : "end of period"}.
              </div>
            </div>
          )}
          {status.trial_end && status.status === "trialing" && (
            <div className="font-mono text-xs text-white/50 mt-2">
              Trial ends {new Date(status.trial_end * 1000).toLocaleDateString()}.
            </div>
          )}
          <button
            onClick={openPortal}
            disabled={opening}
            data-testid="billing-portal-btn"
            className="btn-forged-ghost mt-5 disabled:opacity-50"
          >
            {opening ? <><Loader2 className="w-4 h-4 animate-spin" /> Opening</> : <><ExternalLink className="w-4 h-4" /> Manage Subscription</>}
          </button>
        </>
      ) : canManage ? (
        // Has a customer record (canceled/expired) — show resubscribe + manage
        <>
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="w-4 h-4 text-white/40" />
            <div className="font-mono text-[10px] tracking-[0.3em] text-white/50">
              {status.status?.toUpperCase()} · MEMBERSHIP INACTIVE
            </div>
          </div>
          <div className="font-sub text-white/70 leading-relaxed mt-2">
            Your subscription has ended. Resubscribe to unlock the full system again.
          </div>
          <div className="flex flex-wrap gap-3 mt-5">
            <Link to="/pricing" data-testid="billing-resubscribe-link" className="btn-forged-red">
              Resubscribe · $9.99/mo
            </Link>
            <button
              onClick={openPortal}
              disabled={opening}
              data-testid="billing-portal-btn"
              className="btn-forged-ghost disabled:opacity-50"
            >
              {opening ? <><Loader2 className="w-4 h-4 animate-spin" /> Opening</> : <><ExternalLink className="w-4 h-4" /> Billing History</>}
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="font-sub text-white/70 leading-relaxed">
            You&apos;re not a Forged member yet. Subscribe to unlock the full AI system — workouts, nutrition, coach chat, and trackers.
          </div>
          <Link to="/pricing" data-testid="billing-subscribe-link" className="btn-forged-red mt-5 inline-flex">
            Join Now · $9.99/mo
          </Link>
        </>
      )}
    </div>
  );
}
